/**
 * LiveDataGuard (v6.36.26) — Super-admin panel that:
 *   1. Lets Cesar mint a 10-minute owner-approval token for destructive ops
 *   2. Lists currently-active tokens (read-only)
 *   3. Shows the destructive-ops audit log (every blocked / approved attempt)
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ShieldAlert, Key, Copy, Loader2, History, X, CheckCircle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LiveDataGuard = () => {
  const [scope, setScope] = useState('');
  const [activeTokens, setActiveTokens] = useState([]);
  const [logs, setLogs] = useState([]);
  const [protectedCollections, setProtectedCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minting, setMinting] = useState(false);
  const [lastMinted, setLastMinted] = useState(null);
  const [onlyBlocked, setOnlyBlocked] = useState(false);

  const fetchData = async (filter = onlyBlocked) => {
    setLoading(true);
    try {
      const [tokensRes, logRes] = await Promise.all([
        axios.get(`${API}/governance/approval-token/active`),
        axios.get(`${API}/governance/destructive-ops/log`, { params: { only_blocked: filter, limit: 100 } }),
      ]);
      setActiveTokens(tokensRes.data?.tokens || []);
      setLogs(logRes.data?.items || []);
      setProtectedCollections(logRes.data?.protected_collections || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load Live Data Guard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleMint = async () => {
    if (scope.trim().length < 5) {
      toast.error('Explain why (at least 5 characters)');
      return;
    }
    setMinting(true);
    try {
      const res = await axios.post(`${API}/governance/approval-token/mint`, { scope: scope.trim() });
      setLastMinted(res.data);
      toast.success('Approval token minted (10 min validity)');
      setScope('');
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mint token');
    } finally {
      setMinting(false);
    }
  };

  const copyToken = (t) => {
    navigator.clipboard?.writeText(t).then(() => toast.success('Token copied'));
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="live-data-guard-page">
      <div className="flex items-center gap-3 mb-2">
        <Link to="/admin"><Button variant="ghost" size="sm" className="h-8 px-2"><ArrowLeft size={18} /></Button></Link>
        <ShieldAlert className="w-7 h-7 text-red-600" />
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Live Data Guard</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6 ml-12">
        v6.36.26 — Cesar Directive runtime safeguards. {protectedCollections.length} collections under L1 protection.
      </p>

      {/* Mint approval token */}
      <div className="border-2 border-red-500 p-5 mb-8" data-testid="mint-token-section">
        <div className="flex items-center gap-2 mb-3">
          <Key className="w-5 h-5 text-red-600" />
          <h2 className="font-bold text-base uppercase tracking-wider">Mint Approval Token</h2>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Generates a 10-minute single-use token authorizing one destructive operation on L1 protected data. Owner-only (Cesar Roca / super_admin).
        </p>
        <textarea
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          rows={2}
          placeholder='Why do you need this? Example: "Delete duplicate test district acme_test_123"'
          className="w-full border-2 border-gray-300 p-2 text-sm font-mono focus:border-[#002FA7] focus:outline-none mb-3"
          data-testid="mint-scope-input"
        />
        <Button
          onClick={handleMint}
          disabled={minting}
          className="bg-red-600 hover:bg-red-700 text-white"
          data-testid="mint-token-btn"
        >
          {minting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Minting…</> : <><Key className="w-4 h-4 mr-2" /> Mint 10-Minute Token</>}
        </Button>

        {lastMinted && (
          <div className="mt-4 p-4 bg-amber-50 border-l-4 border-amber-500" data-testid="minted-token-display">
            <div className="text-xs font-bold uppercase text-gray-600 mb-2">New Token (expires {new Date(lastMinted.expires_at).toLocaleTimeString()})</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all text-xs bg-white p-2 border border-amber-200" data-testid="minted-token-value">{lastMinted.token}</code>
              <Button size="sm" variant="outline" onClick={() => copyToken(lastMinted.token)} className="btn-sharp" data-testid="copy-token-btn">
                <Copy className="w-3 h-3 mr-1" /> Copy
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">Pass this token as the `approval_token` parameter on a destructive op call. It will be burned after one use.</p>
          </div>
        )}
      </div>

      {/* Active tokens */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-5 h-5 text-gray-500" />
          <h2 className="font-bold text-base uppercase tracking-wider">Active Tokens ({activeTokens.length})</h2>
        </div>
        {activeTokens.length === 0 && <p className="text-sm text-gray-400 italic">No active tokens.</p>}
        {activeTokens.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="active-tokens-list">
            {activeTokens.map((t) => (
              <div key={t.token} className="border border-gray-300 p-3 text-xs" data-testid={`active-token-${t.token.slice(0, 8)}`}>
                <div className="font-mono break-all text-gray-700 mb-1">{t.token.slice(0, 20)}…</div>
                <div className="text-gray-500">Scope: {t.scope}</div>
                <div className="text-gray-500">Minted: {new Date(t.minted_at).toLocaleTimeString()} → Expires: {new Date(t.expires_at).toLocaleTimeString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Destructive-op audit log */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-gray-500" />
            <h2 className="font-bold text-base uppercase tracking-wider">Destructive-Op Audit Log ({logs.length})</h2>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={onlyBlocked}
              onChange={(e) => { setOnlyBlocked(e.target.checked); fetchData(e.target.checked); }}
              data-testid="filter-only-blocked"
            />
            Only blocked
          </label>
        </div>
        {loading && <div className="text-center py-8 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
        {!loading && logs.length === 0 && <p className="text-sm text-gray-400 italic">No destructive-op attempts yet — that's a good thing.</p>}
        {logs.length > 0 && (
          <div className="border border-gray-300 divide-y divide-gray-200" data-testid="audit-log-list">
            {logs.map((row, i) => (
              <div key={i} className="p-3 grid grid-cols-1 sm:grid-cols-12 gap-2 text-sm items-center" data-testid={`audit-row-${i}`}>
                <div className="col-span-1">
                  {row.approved ? (
                    <CheckCircle className="w-4 h-4 text-green-600" aria-label="Approved" />
                  ) : (
                    <X className="w-4 h-4 text-red-600" aria-label="Blocked" />
                  )}
                </div>
                <div className="col-span-2 font-mono text-xs text-gray-500">{new Date(row.at).toLocaleString()}</div>
                <div className="col-span-2 font-bold">{row.collection}</div>
                <div className="col-span-2 text-gray-600">{row.operation}</div>
                <div className="col-span-3 text-gray-700 text-xs">{row.actor_email || row.actor_user_id || '(no actor)'}</div>
                <div className="col-span-2 text-xs text-gray-500">{row.reason}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Protected collections reference */}
      <div className="mt-10 pt-6 border-t border-gray-200">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">L1 Protected Collections</h3>
        <div className="flex flex-wrap gap-1.5">
          {protectedCollections.map((c) => (
            <span key={c} className="text-xs px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 font-mono">{c}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LiveDataGuard;
